import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Divider,
  Typography,
  Tooltip
} from "antd";

import BlogSelector from "./BlogNavigationSelect";
import { createSlug } from "../../../../../utils/helper.jsx";
import { BLOG_PUBLISH_STATUS } from "../../BlogsDashboard";

const { TextArea } = Input;
const { Title } = Typography;
const { Item } = Form;

export const FormStep1 = ({
  formStep1,
  handleSaveStep1,
  handlePublishBlog,
  slugTouchedRef,
  authorsLoading,
  authorsOptions,
  setAuthorModalOpen,
  renderStepControls,
  basicBlogDetailsList = []
}) => {
  const { Option } = Select;
  return (
    <Form
      layout="vertical"
      form={formStep1}
      onFinish={handleSaveStep1}
      initialValues={{
        readMinutes: 10,
        tags: [],
        authorIds: [],
        publishStatus: BLOG_PUBLISH_STATUS.DRAFT
      }}
      onValuesChange={(changedValues) => {
        if (changedValues.title && !slugTouchedRef.current) {
          formStep1.setFieldsValue({ slug: createSlug(changedValues.title) });
        }
      }}
    >
      <Title level={4}>Basic Information</Title>

      <Item
        label="Blog Publish Status"
        tooltip="Controls the publishing stage of this blog"
        required
      >
        <Space wrap style={{ display: "flex", width: "100%" }}>
          <Item
            name="publishStatus"
            noStyle
            rules={[{ required: true, message: "Please select a blog status" }]}
          >
            <Select placeholder="Select blog status" style={{ flex: 1 }}>
              {Object.entries(BLOG_PUBLISH_STATUS)
                .filter(([_, value]) =>
                  formStep1.originalPublishStatus === BLOG_PUBLISH_STATUS.PUBLISHED
                    ? true
                    : value !== BLOG_PUBLISH_STATUS.PUBLISHED
                )
                .map(([key, value]) => (
                  <Option key={key} value={value}>
                    {value}
                  </Option>
                ))}
            </Select>
          </Item>

          <Tooltip title="If the blog is already published, clicking publish again will republish the blog. This should be used to push draft content to live content if modified in published state. This will not notify users but will update draft content to live content.">
            <Button type="primary" onClick={() => handlePublishBlog()}>
              Publish <InfoCircleOutlined style={{ marginLeft: 4 }} />
            </Button>
          </Tooltip>
        </Space>
      </Item>

      <Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
        <Input placeholder="Dynamic Programming Explained" />
      </Item>

      <Item
        name="slug"
        label="Slug"
        rules={[
          { required: true, message: "Slug is required" },
          {
            pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
            message: "Slug must be lowercase words joined by '-'"
          }
        ]}
      >
        <Input
          placeholder="dynamic-programming-explained"
          onChange={() => (slugTouchedRef.current = true)}
        />
      </Item>

      <Item
        name="displayOrderUnderParent"
        label="Display Order"
        tooltip="Controls order within the same parent"
      >
        <InputNumber min={0} style={{ width: "100%" }} />
      </Item>

      <Item
        name="summary"
        label="Summary"
        rules={[{ required: true, message: "Summary is required" }]}
      >
        <TextArea rows={3} placeholder="Brief summary of the blog..." />
      </Item>

      <Item name="readMinutes" label="Estimated Read Time (min)" rules={[{ required: true }]}>
        <InputNumber min={1} style={{ width: "100%" }} />
      </Item>

      <Item
        name="authorIds"
        label="Author(s)"
        rules={[{ required: true, message: "Please select at least one blog author" }]}
      >
        <Select
          mode="multiple"
          placeholder="Select Author(s)"
          showSearch
          filterOption={false}
          notFoundContent={authorsLoading ? "Loading..." : "No authors"}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <Space style={{ padding: "0 8px 4px" }}>
                <Button type="link" onClick={() => setAuthorModalOpen(true)}>
                  + Add New Author
                </Button>
              </Space>
            </>
          )}
          optionFilterProp="children"
        >
          {authorsOptions?.authors?.map((author) => (
            <Option key={author._id} value={author._id}>
              {author.name}
            </Option>
          ))}
        </Select>
      </Item>

      <Item name="tags" label="Tags (comma separated)">
        <Select mode="tags" tokenSeparators={[","]} placeholder="dp, recursion, graphs" />
      </Item>

      <Divider />

      <BlogSelector
        name="previousBlogIdentifier"
        label="Previous Blog"
        placeholder="Select previous blog"
        basicBlogDetailsList={basicBlogDetailsList}
        onFieldClear={(fieldName) => formStep1.setFieldsValue({ [fieldName]: null })}
      />

      <Divider />

      <BlogSelector
        name="nextBlogIdentifier"
        label="Next Blog"
        placeholder="Select next blog"
        basicBlogDetailsList={basicBlogDetailsList}
        onFieldClear={(fieldName) => formStep1.setFieldsValue({ [fieldName]: null })}
      />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>{renderStepControls()}</div>
    </Form>
  );
};
